import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BidAskMidPicker } from '@components/BidAskMidPicker';
import { SlideToConfirmButton } from '@components/SlideToConfirmButton';
import { useAccount } from '@hooks/useAccount';
import { useAccountService } from '@hooks/useAccountService';
import { useOrders } from '@hooks/useOrders';
import type { AggregatedPosition } from '@hooks/usePositionAggregation';
import { orderService } from '@services/orderService';
import { PnLService } from '@services/pnlService';
import { priceService } from '@services/priceService';
import { volatilityService } from '@services/volatilityService';
import { brandColors } from '@utils/color-utils';

interface ClosePositionPanelProps {
  isVisible: boolean;
  onClose: () => void;
  positionGroup: AggregatedPosition | null;
}

export function ClosePositionPanel({
  isVisible,
  onClose,
  positionGroup,
}: ClosePositionPanelProps) {
  const accountService = useAccountService();
  const { openPnl } = useAccount();
  const { openOrders } = useOrders();
  const [orderType, setOrderType] = useState<'MARKET' | 'LIMIT'>('MARKET');
  const [limitPrice, setLimitPrice] = useState(0);
  const hasInitializedRef = useRef(false);

  // Calculate live open sell orders for this position group
  const liveOpenSellOrders = useMemo(() => {
    if (!positionGroup) {
      return [];
    }
    const positionIds = positionGroup.positions.map(p => p.id);
    return openOrders
      .filter(
        order =>
          order.side === 'SELL' &&
          order.status === 'PENDING' &&
          order.positionId &&
          positionIds.includes(order.positionId),
      )
      .map(order => ({
        id: order.id,
        quantity: order.quantity,
        orderType: order.orderType,
        limitPrice: order.limitPrice,
      }));
  }, [positionGroup, openOrders]);

  // Get spot price for reactivity
  const spotPrice = useMemo(() => {
    if (!positionGroup?.positions.length) {
      return null;
    }
    const firstPosition = positionGroup.positions[0];
    return priceService.getPrice(firstPosition.asset)?.price ?? null;
  }, [positionGroup, openPnl]); // openPnl changes trigger price updates

  // Check if there are multiple strikes
  const hasMultipleStrikes = useMemo(() => {
    if (!positionGroup?.positions.length) {
      return false;
    }
    const firstStrike = positionGroup.positions[0].strikePrice;
    return positionGroup.positions.some(pos => pos.strikePrice !== firstStrike);
  }, [positionGroup]);

  // Calculate weighted average pricing across all positions
  const currentPricing = useMemo(() => {
    if (!positionGroup?.positions.length) {
      return null;
    }

    if (!hasMultipleStrikes) {
      // Single strike - use first position's pricing
      const firstPosition = positionGroup.positions[0];
      return orderService.calculatePositionPricing(firstPosition);
    }

    // Multiple strikes - calculate weighted averages
    let totalQuantity = 0;
    let weightedBid = 0;
    let weightedAsk = 0;
    let weightedMid = 0;

    for (const position of positionGroup.positions) {
      const pricing = orderService.calculatePositionPricing(position);
      if (!pricing) {
        continue;
      }

      const qty = position.quantity;
      totalQuantity += qty;
      weightedBid += pricing.bid * qty;
      weightedAsk += pricing.ask * qty;
      weightedMid += pricing.mid * qty;
    }

    if (totalQuantity === 0) {
      return null;
    }

    return {
      bid: weightedBid / totalQuantity,
      ask: weightedAsk / totalQuantity,
      mid: weightedMid / totalQuantity,
    };
  }, [positionGroup, spotPrice, hasMultipleStrikes]);

  // Reset to market order and mid price when panel becomes visible (only once per open)
  useEffect(() => {
    if (isVisible && !hasInitializedRef.current && currentPricing) {
      setOrderType('MARKET');
      setLimitPrice(currentPricing.bid);
      hasInitializedRef.current = true;
    }

    // Reset initialization flag when panel closes
    if (!isVisible) {
      hasInitializedRef.current = false;
    }
  }, [isVisible, currentPricing]);

  // Calculate the effective close price
  const effectivePrice = useMemo(() => {
    if (!currentPricing) {
      return null;
    }
    return orderType === 'MARKET' ? currentPricing.bid : limitPrice;
  }, [orderType, currentPricing, limitPrice]);

  // Calculate PnL preview with effective price
  const pnlPreview = useMemo(() => {
    if (!positionGroup?.positions.length || effectivePrice === null) {
      return null;
    }

    return positionGroup.positions.reduce((total, position) => {
      const totalCost = position.purchasePrice * position.quantity;
      const saleValue = effectivePrice * position.quantity;
      return total + (saleValue - totalCost);
    }, 0);
  }, [positionGroup, effectivePrice]);

  // Calculate live PnL for the specific positions in this group
  const livePnL = useMemo(() => {
    if (!positionGroup?.positions.length) {
      return 0;
    }

    return positionGroup.positions.reduce((total, position) => {
      try {
        const spotPrice = priceService.getPrice(position.asset)?.price;
        const volatility = volatilityService.getVolatility(position.asset);

        if (!spotPrice || !volatility || volatility <= 0) {
          return total;
        }

        const pnlData = PnLService.calculatePositionPnL(
          position,
          spotPrice,
          volatility,
          Date.now(),
        );

        return total + pnlData.pnlTotal;
      } catch (error) {
        console.warn('Error calculating position PnL:', error);
        return total;
      }
    }, 0);
  }, [openPnl, positionGroup]);

  const handleCloseAllPositions = useCallback(async () => {
    if (
      !positionGroup ||
      !accountService ||
      effectivePrice === null ||
      !currentPricing
    ) {
      return;
    }

    try {
      // Calculate individual limit prices for each position
      // If user entered a limit price, it's the weighted average they want
      // We need to calculate individual prices maintaining the same offset
      const limitPriceOffset =
        orderType === 'LIMIT' && hasMultipleStrikes
          ? limitPrice - currentPricing.bid
          : 0;

      // Close all positions in this group
      for (const position of positionGroup.positions) {
        let positionLimitPrice: number | undefined;

        if (orderType === 'LIMIT') {
          if (hasMultipleStrikes) {
            // Calculate individual position's limit price
            const positionPricing =
              orderService.calculatePositionPricing(position);
            if (positionPricing) {
              // Apply the same offset from weighted average to this position's bid
              positionLimitPrice = positionPricing.bid + limitPriceOffset;
            }
          } else {
            // Single strike - use the limit price directly
            positionLimitPrice = limitPrice;
          }
        }

        orderService.createOrder({
          asset: position.asset,
          type: position.type,
          strikePrice: position.strikePrice,
          quantity: position.quantity,
          orderType,
          limitPrice: positionLimitPrice,
          side: 'SELL',
          expiryTimestamp: position.expiryTimestamp,
          positionId: position.id,
        });
      }

      onClose();
    } catch (error) {
      console.error('Error closing positions:', error);
    }
  }, [
    positionGroup,
    onClose,
    accountService,
    orderType,
    effectivePrice,
    currentPricing,
    hasMultipleStrikes,
    limitPrice,
  ]);

  const formatPnL = (pnl: number) => {
    const absValue = Math.abs(pnl);
    if (absValue >= 1000) {
      return `${pnl >= 0 ? '+' : ''}$${(pnl / 1000).toFixed(1)}k`;
    }
    return `${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)}`;
  };

  if (!positionGroup) {
    return null;
  }

  // If there are open sell orders, show cancel UI instead
  if (liveOpenSellOrders.length > 0) {
    return (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="flex-shrink-0 bg-brand-dark/95 backdrop-blur-sm border-t-2 border-yellow-500/50 overflow-hidden"
      >
        <div className="p-4 space-y-4">
          {/* Header */}
          <div className="text-center">
            <div className="text-yellow-500 text-sm font-semibold uppercase tracking-wider mb-2">
              Open Sell Orders
            </div>
            <div className="text-white/70 text-sm">
              This position already has{' '}
              {liveOpenSellOrders.length === 1
                ? 'an open sell order'
                : `${liveOpenSellOrders.length} open sell orders`}
              . Cancel {liveOpenSellOrders.length === 1 ? 'it' : 'them'} to
              create a new order.
            </div>
          </div>

          {/* Order Details */}
          <div className="space-y-2">
            {liveOpenSellOrders.map(order => (
              <div
                key={order.id}
                className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-white font-medium">
                      {order.quantity} {positionGroup.dominantType}
                    </div>
                    <div className="text-yellow-500/80 text-xs">
                      {order.orderType}
                      {order.orderType === 'LIMIT' && order.limitPrice
                        ? ` @ $${order.limitPrice.toFixed(2)}`
                        : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      orderService.cancelOrder(order.id);
                      // Don't close panel - let user cancel more if needed
                    }}
                    className="px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-500 rounded-lg text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Cancel All Button */}
          {liveOpenSellOrders.length > 1 && (
            <button
              onClick={() => {
                liveOpenSellOrders.forEach(order =>
                  orderService.cancelOrder(order.id),
                );
                onClose();
              }}
              className="w-full py-3 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-500 rounded-lg font-semibold transition-colors"
            >
              Cancel All Orders
            </button>
          )}

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    );
  }

  const expectedPnL = pnlPreview ?? livePnL;
  const isProfit = expectedPnL > 0;
  const totalCost = positionGroup.positions.reduce(
    (sum, pos) => sum + pos.purchasePrice * pos.quantity,
    0,
  );

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className="flex-shrink-0 bg-brand-dark/95 backdrop-blur-sm border-t-2 border-divider overflow-hidden"
      style={{
        maxHeight: '50vh',
      }}
    >
      <div className="p-4 space-y-3">
        {/* Position Summary and Price Selection */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          {/* Left: Position Summary */}
          <div className="col-span-1 flex flex-col justify-center">
            <div className="text-white/60 text-xs uppercase tracking-wider mb-1">
              {`Closing ${positionGroup.positions.length} `}
              {positionGroup.positions.length === 1 ? 'Position' : 'Positions'}
            </div>
            <div className="text-2xl font-bold">
              <span
                style={{
                  color:
                    positionGroup.dominantType === 'CALL'
                      ? brandColors.green
                      : brandColors.red,
                }}
              >
                {positionGroup.dominantType === 'CALL' ? '↑' : '↓'}
              </span>{' '}
              <span className="text-white">
                {positionGroup.totalQuantity.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>

          {/* Right: Order Type and Price Selection */}
          <div className="col-span-3 space-y-2">
            {/* Order Type Toggle */}
            <div className="flex justify-between items-center text-xs">
              <span className="text-label">
                {orderType === 'MARKET' ? 'MARKET PRICE' : 'LIMIT PRICE'}
                {hasMultipleStrikes && ' (AVERAGE)'}
              </span>
              <button
                onClick={() =>
                  setOrderType(orderType === 'MARKET' ? 'LIMIT' : 'MARKET')
                }
                className="text-white text-xs flex items-center space-x-1"
              >
                <span>
                  {orderType === 'MARKET' ? 'Market Order' : 'Limit Order'}
                </span>
                <span className="text-label">▼</span>
              </button>
            </div>

            {/* Price Picker */}
            {orderType === 'MARKET' ? (
              <div className="bg-surface/50 border border-border-subtle rounded p-2 text-center">
                <div className="text-lg font-mono text-label">
                  ${currentPricing?.bid.toFixed(2)}
                </div>
              </div>
            ) : (
              <BidAskMidPicker
                pricing={currentPricing}
                selectedPrice={limitPrice}
                onPriceChange={setLimitPrice}
                isLoading={!currentPricing}
                disabled={false}
              />
            )}
          </div>
        </div>

        {/* Trade Stats */}
        <div className="grid grid-cols-3 gap-3 text-xs mb-4">
          <div className="text-center">
            <div className="text-label">COST BASIS</div>
            <div className="text-white text-lg">${totalCost.toFixed(2)}</div>
          </div>
          <div className="text-center">
            <div className="text-label">SELL PRICE</div>
            <div className="text-white text-lg">
              ${effectivePrice?.toFixed(2)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-label">EXPECTED PnL</div>
            <div
              className="text-lg font-bold"
              style={{
                color: isProfit ? brandColors.green : brandColors.red,
              }}
            >
              {formatPnL(expectedPnL)}
            </div>
          </div>
        </div>

        {/* Order Description */}
        <div className="mb-4 text-center text-xs text-white/50">
          {orderType === 'MARKET'
            ? hasMultipleStrikes
              ? 'Will execute immediately at current bid prices (weighted average shown)'
              : 'Will execute immediately at current bid price'
            : hasMultipleStrikes
              ? `Will create limit sell orders averaging $${effectivePrice?.toFixed(2)}`
              : `Will create limit sell order at $${effectivePrice?.toFixed(2)}`}
        </div>

        {/* Slide to Confirm */}
        <SlideToConfirmButton
          onConfirm={handleCloseAllPositions}
          disabled={effectivePrice === null}
          className="w-full bg-red-500/80"
          confirmText="RELEASE TO CONFIRM"
        >
          <span>
            SLIDE TO CLOSE •{' '}
            <span
              style={{
                color: isProfit ? brandColors.green : brandColors.red,
              }}
            >
              {formatPnL(expectedPnL)}
            </span>
          </span>
        </SlideToConfirmButton>
      </div>
    </motion.div>
  );
}
